/**
 * SourceMapReviewPage — Admin page for reviewing LLM-extracted source
 * coordinate maps. Thin wrapper that wires hooks → ExtractionReviewDashboard.
 *
 * Route: /admin/source-maps/:mappingId
 *
 * FERP Principles:
 * - Dumb Frontend: All extraction data from backend GraphQL
 * - Data-driven: value_type drives rendering, edi_context is nullable
 * - Repository pattern: All data access through Apollo hooks
 */

import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Text,
  Breadcrumbs,
  Anchor,
  Alert,
  Loader,
  Center,
  Group,
  Badge,
  Title,
  Paper,
} from '@mantine/core';

import { useTenantId } from '@/auth';
import {
  ExtractionReviewDashboard,
  useSourceMap,
  useSourceMaps,
  useSourceMapStats,
  useVerifyExtraction,
  useRejectExtraction,
  useBulkVerifyExtractions,
} from '@ferp/workflow-forms';

/**
 * Source Map Review — single map detail view.
 * Reads :mappingId from URL params, tenantId from auth context.
 */
export function SourceMapReviewPage() {
  const { mappingId } = useParams<{ mappingId: string }>();
  const tenantId = useTenantId();
  const navigate = useNavigate();

  const { sourceMap, loading, error } = useSourceMap(
    tenantId ?? '',
    mappingId ?? '',
  );
  const { verify } = useVerifyExtraction();
  const { reject } = useRejectExtraction();
  const { bulkVerify } = useBulkVerifyExtractions();

  if (!tenantId) {
    return (
      <Center h="50vh">
        <Loader size="xl" type="dots" />
      </Center>
    );
  }

  if (!mappingId) {
    return (
      <Container size="lg" py="md">
        <Alert color="red" title="Missing mapping ID">
          No source map mapping ID provided in the URL.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/admin/source-maps')}>
            Source Maps
          </Anchor>
          <Text size="sm">{mappingId}</Text>
        </Breadcrumbs>

        <ExtractionReviewDashboard
          sourceMap={sourceMap}
          loading={loading}
          error={error}
          onVerifyExtraction={(extractionId, status, note) =>
            verify(tenantId, mappingId, extractionId, 'system', status, note)
          }
          onRejectExtraction={(extractionId, reason) =>
            reject(tenantId, mappingId, extractionId, 'system', reason)
          }
          onBulkVerify={(threshold) =>
            bulkVerify(tenantId, mappingId, 'system', threshold)
          }
          onComplete={() => navigate('/admin/source-maps')}
        />
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Source Map List Item type (matches GraphQL query shape)
// ---------------------------------------------------------------------------

interface SourceMapListItem {
  mappingId: string;
  sourceDocument: {
    documentId: string;
    filename: string;
    totalPages: number | null;
  };
  ingestedAt: string;
  extractor: {
    provider: string;
    model: string;
  };
  reviewStatus: string;
  summary: {
    totalExtractions: number;
    verifiedCount: number;
    rejectedCount: number;
    unverifiedCount: number;
    averageConfidence: number;
  };
}

/**
 * Source Map List — overview of all source maps for the tenant.
 * Shows review status, extraction counts, and average confidence.
 */
export function SourceMapListPage() {
  const tenantId = useTenantId();
  const navigate = useNavigate();
  const { sourceMaps, loading, error } = useSourceMaps(tenantId ?? '');
  const { stats } = useSourceMapStats(tenantId ?? '');

  if (!tenantId) {
    return (
      <Center h="50vh">
        <Loader size="xl" type="dots" />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Stack gap={4}>
            <Title order={3}>Source Coordinate Maps</Title>
            <Text size="sm" c="dimmed">
              Review LLM-extracted values traced back to source document
              coordinates.
            </Text>
          </Stack>
          {stats && (
            <Group gap="xs">
              <Badge variant="filled" color="blue">
                {stats.totalMaps} maps
              </Badge>
              <Badge variant="filled" color="orange">
                {stats.pendingReview} pending
              </Badge>
              <Badge variant="filled" color="green">
                {stats.verified} verified
              </Badge>
            </Group>
          )}
        </Group>

        {error && (
          <Alert color="red" title="Error loading source maps">
            {error}
          </Alert>
        )}

        {loading ? (
          <Center py="xl">
            <Loader size="lg" type="dots" />
          </Center>
        ) : sourceMaps.length === 0 ? (
          <Alert color="gray" title="No source maps">
            No source coordinate maps have been ingested yet. Seed source map
            configurations to get started.
          </Alert>
        ) : (
          <Stack gap="sm">
            {sourceMaps.map((map: SourceMapListItem) => (
              <SourceMapCard
                key={map.mappingId}
                map={map}
                onClick={() =>
                  navigate(`/admin/source-maps/${map.mappingId}`)
                }
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Internal: Source Map List Card
// ---------------------------------------------------------------------------

function SourceMapCard({
  map,
  onClick,
}: {
  map: SourceMapListItem;
  onClick: () => void;
}) {
  const reviewColor =
    map.reviewStatus === 'verified'
      ? 'green'
      : map.reviewStatus === 'rejected'
        ? 'red'
        : 'orange';

  const pct =
    map.summary.totalExtractions > 0
      ? Math.round(
          (map.summary.verifiedCount / map.summary.totalExtractions) * 100,
        )
      : 0;

  return (
    <Paper
      withBorder
      p="md"
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    >
      <Group justify="space-between">
        <Stack gap={4}>
          <Text fw={600}>{map.sourceDocument.filename}</Text>
          <Text size="xs" c="dimmed">
            {map.mappingId} | {map.extractor.provider}/{map.extractor.model} |
            Ingested {new Date(map.ingestedAt).toLocaleDateString()}
          </Text>
        </Stack>
        <Group gap="xs">
          <Badge variant="light" color={reviewColor}>
            {map.reviewStatus.replace('_', ' ')}
          </Badge>
          <Badge variant="outline">
            {map.summary.verifiedCount}/{map.summary.totalExtractions}{' '}
            verified ({pct}%)
          </Badge>
          <Badge variant="dot" color="blue">
            {Math.round(map.summary.averageConfidence * 100)}% avg
          </Badge>
        </Group>
      </Group>
    </Paper>
  );
}
